import {
  Button,
  DatePicker,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@heroui/react';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '@/redux/hooks';
import { useUpdatePatientMutation } from '@/patient-app-common/api/patientApi';
import { setDischargePrompt } from '@/patient-app-common/slices/appSlice';

interface SetDeliveryProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SetDelivery({ isOpen, onClose }: SetDeliveryProps) {
  const { t } = useTranslation('home');
  const dispatch = useAppDispatch();

  const [updatePatient, { isLoading }] = useUpdatePatientMutation();

  const [page, setPage] = useState(1);

  const [date, setDate] = useState<null | string>(null);
  const [error, setError] = useState('');

  const validateDate = () => {
    let valid = true;

    if (!dayjs(date).isValid()) {
      setError(t('common.error.blank_input'));
      return false;
    }

    if (dayjs(date).startOf('day').isAfter(dayjs().startOf('day'))) {
      setError(t('setdelivery.error'));
      valid = false;
    }

    return valid;
  };

  const handleSubmit = async () => {
    const valid = validateDate();
    if (!valid) return;

    const params = {
      confirmedSurgery: true,
      surgeryDate: dayjs(date).locale('en').format('MM/DD/YYYY'),
    };

    const result = await updatePatient(params).unwrap();

    dispatch(setDischargePrompt(false));

    if (result.success) {
      setError('');
      onClose();
    } else {
      setError(t('common.error.server'));
    }
  };

  const resetToPreop = async () => {
    const params = { confirmedSurgery: false };
    await updatePatient(params);
    handleClose();
  };

  const handleClose = () => {
    dispatch(setDischargePrompt(false));
    onClose();
    setPage(1);
  };

  return (
    <Modal
      isOpen={isOpen}
      isDismissable={false}
      hideCloseButton
      onClose={onClose}
      size="2xl"
    >
      <ModalContent>
        {page === 1 && (
          <>
            <ModalHeader>{t('setdelivery.title')}</ModalHeader>
            <ModalBody>
              <p>{t('setdelivery.description')}</p>
            </ModalBody>
            <ModalFooter>
              <Button
                color="primary"
                isLoading={isLoading}
                onPress={() => setPage(2)}
              >
                {t('common.action.yes')}
              </Button>
              <Button color="primary" variant="light" onPress={resetToPreop}>
                {t('common.action.no')}
              </Button>
            </ModalFooter>
          </>
        )}

        {page === 2 && (
          <>
            <ModalHeader>{t('setdelivery.input.label')}</ModalHeader>
            <ModalBody>
              <DatePicker
                label={t('common.input.date')}
                errorMessage={error}
                isInvalid={!_.isEmpty(error)}
                onChange={(date) => setDate(_.toString(date))}
              />
            </ModalBody>
            <ModalFooter>
              <Button
                color="primary"
                isLoading={isLoading}
                onPress={handleSubmit}
              >
                {t('common.action.save')}
              </Button>
              <Button color="danger" variant="light" onPress={handleClose}>
                {t('common.action.cancel')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
